import { useEffect, useState } from "react";

import { API_ROUTES } from "@/config/apiRoutes";
import { useAuthStore } from "@/stores/AuthStore";
import { useOrganisationStore } from "@/stores/OrganisationStore";
import { loggerService, LogTag } from "@/Utils/LoggerService";
import appFetch from "@/Utils/Services/Fetch/appFetch";

interface NavCounts {
  events: number | null;
  volunteers: number | null;
  contracts: number | null;
}

interface EventsCountResponse {
  pagination?: { total?: number };
}

interface VolunteersCountResponse {
  total?: number;
}

interface ContractsStatsResponse {
  data?: { total?: number };
}

const EMPTY_COUNTS: NavCounts = {
  events: null,
  volunteers: null,
  contracts: null,
};

/**
 * Loads the counts displayed as badges in the navbar (events, volunteers).
 * Fetched once when the session and organisation become available.
 */
const useNavCounts = (): NavCounts => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const orgId = useOrganisationStore((state) => state.organisation?.org_id);
  const [counts, setCounts] = useState<NavCounts>(EMPTY_COUNTS);

  useEffect(() => {
    if (!isAuthenticated) {
      setCounts(EMPTY_COUNTS);
      return;
    }

    let cancelled = false;

    const fetchCounts = async () => {
      try {
        const eventsParams = new URLSearchParams({ page: "1", limit: "1" });
        const eventsResponse = (await appFetch(
          `${API_ROUTES.events.list}?${eventsParams.toString()}`,
        )) as EventsCountResponse | null;

        let volunteersTotal: number | null = null;
        if (orgId) {
          const volunteersParams = new URLSearchParams({
            org_id: String(orgId),
            page: "1",
            limit: "1",
          });
          const volunteersResponse = (await appFetch(
            `${API_ROUTES.volunteers.list}?${volunteersParams.toString()}`,
          )) as VolunteersCountResponse | null;
          volunteersTotal = volunteersResponse?.total ?? null;
        }

        let contractsCount: number | null = null;
        if (orgId) {
          const contractsResponse = (await appFetch(
            `${API_ROUTES.contracts.stats}?org_id=${String(orgId)}`,
          )) as ContractsStatsResponse | null;

          if (contractsResponse?.data) {
            contractsCount = contractsResponse.data.total ?? 0;
          }
        }

        if (cancelled) return;

        setCounts({
          events: eventsResponse?.pagination?.total ?? null,
          volunteers: volunteersTotal,
          contracts: contractsCount,
        });
      } catch (error) {
        if (!cancelled) {
          loggerService.error(
            LogTag.API,
            "useNavCounts: failed to load navigation counts",
            error,
          );
        }
      }
    };

    void fetchCounts();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, orgId]);

  return counts;
};

export default useNavCounts;
